import { memo, forwardRef, useState, useMemo, useCallback } from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '../../lib/styles';
import { TableListItemValueProps } from './TableList.types';
import { useChildPosition } from '@page/core/hooks/dom/useChildPosition';
import { Divider } from '../Divider';
import { TableListItemValueContext } from './TableListItemValue.context';
import { useMergedRefs } from '@page/core/hooks/common';

const TableListItemValueBase = (
  {
    children,
    actions,
    alwaysDisplayActions = false,
    onClick,
    asChild,
    className,
    dividerClassName,
    active = false,
    showDivider,
    actionsClassName,
    ...rest
  }: TableListItemValueProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) => {
  const { ref: refForChildPosition, isLastItem } = useChildPosition();
  const [hasHovered, setHasHovered] = useState(false);
  const ChildrenWrapper = asChild ? Slot : 'div';
  const contextValue = useMemo(() => ({ hasHovered }), [hasHovered]);

  const ref = useMergedRefs(forwardedRef, refForChildPosition);

  const handleMouseEnter = useCallback(() => {
    if (!hasHovered) {
      setHasHovered(true);
    }
  }, [hasHovered]);

  return (
    <TableListItemValueContext.Provider value={contextValue}>
      <div
        ref={ref}
        data-slot="value"
        data-active={active}
        data-is-last={isLastItem}
        className={cn(
          'font-body-md relative text-text-primary p-3 rounded-sm hover:bg-bg-sur-secondary transition-all',
          'max-w-[798px] min-w-[500px] group',
          "data-[active='true']:bg-bg-inter-tertiary-active",
          className,
        )}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        {...rest}
      >
        <ChildrenWrapper>{children}</ChildrenWrapper>

        {actions && (
          <div
            data-slot="actions"
            onClick={onClick ? (event) => event.stopPropagation() : undefined}
            className={cn(
              'transition-all absolute right-4 -top-6 z-10',
              'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:pointer-events-auto focus-within:opacity-100',
              {
                'opacity-100 pointer-events-auto': alwaysDisplayActions,
              },
              actionsClassName,
            )}
          >
            {actions}
          </div>
        )}
      </div>

      {(showDivider ?? !isLastItem) && <Divider className={cn('my-1.5 w-auto', dividerClassName)} />}
    </TableListItemValueContext.Provider>
  );
};

export const TableListItemValue = memo(forwardRef(TableListItemValueBase));
